// Push notifications on this device (core, Phase 1). The service worker in /sw.js receives
// background messages; this file asks permission, registers the token at
// users/{uid}/devices/{token}, and shows foreground messages as toasts.
import { getMessaging, getToken, deleteToken, onMessage, isSupported } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { app, db } from './firebase';
import { ENV } from './env';
import { toast } from './app/toast';

const VAPID = import.meta.env.VITE_FB_VAPID_KEY as string | undefined;
const LS = 'tv.push.token';

export type PushStatus = 'unsupported' | 'unconfigured' | 'blocked' | 'off' | 'on';

export async function pushStatus(): Promise<PushStatus> {
  if (!(await isSupported()) || !('Notification' in window) || !('serviceWorker' in navigator)) return 'unsupported';
  if (!VAPID) return 'unconfigured';
  if (Notification.permission === 'denied') return 'blocked';
  try { return localStorage.getItem(LS) ? 'on' : 'off'; } catch { return 'off'; }
}

export async function registerSW(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try { return await navigator.serviceWorker.register('/sw.js', { scope: '/' }); }
  catch (e) { console.warn('sw register failed', e); return null; }
}

/** Ask for permission and register this device for the signed-in user. Returns the new status. */
export async function enablePush(uid: string, usersPath: string): Promise<PushStatus> {
  const st = await pushStatus();
  if (st === 'unsupported' || st === 'unconfigured' || st === 'blocked') return st;
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'blocked' : 'off';
  const reg = await registerSW();
  if (!reg) return 'unsupported';
  await navigator.serviceWorker.ready;
  const token = await getToken(getMessaging(app), { vapidKey: VAPID, serviceWorkerRegistration: reg });
  await setDoc(doc(db, `${usersPath}/${uid}/devices/${token}`), {
    token, platform: navigator.platform || '', ua: navigator.userAgent.slice(0, 200), app: 'v2',
    env: ENV.name, standalone: matchMedia('(display-mode: standalone)').matches,
    createdAt: serverTimestamp(), lastSeen: serverTimestamp(),
  }, { merge: true });
  try { localStorage.setItem(LS, token); } catch { /* fine */ }
  listenForeground();
  return 'on';
}

export async function disablePush(uid: string, usersPath: string): Promise<PushStatus> {
  let token: string | null = null;
  try { token = localStorage.getItem(LS); localStorage.removeItem(LS); } catch { /* fine */ }
  if (token) {
    await deleteDoc(doc(db, `${usersPath}/${uid}/devices/${token}`)).catch(() => {});
    try { await deleteToken(getMessaging(app)); } catch { /* token may already be gone */ }
  }
  return 'off';
}

/** On app start: if this device registered before, keep it fresh and listen for foreground messages. */
export async function resumePush(uid: string, usersPath: string) {
  if ((await pushStatus()) !== 'on') return;
  let token: string | null = null;
  try { token = localStorage.getItem(LS); } catch { return; }
  if (!token) return;
  const ref = doc(db, `${usersPath}/${uid}/devices/${token}`);
  const snap = await getDoc(ref).catch(() => null);
  if (snap && snap.exists()) await setDoc(ref, { lastSeen: serverTimestamp() }, { merge: true }).catch(() => {});
  listenForeground();
}

let listening = false;
function listenForeground() {
  if (listening) return;
  listening = true;
  onMessage(getMessaging(app), (payload) => {
    // The browser only shows a push by itself when the app is in the background. In the
    // foreground we show it both ways: a toast in the app and a real notification in the
    // tray, so a message is never silently lost while someone has the app open.
    const n = payload.notification ?? {};
    const link = payload.data?.link;
    toast(`${n.title ? n.title + ': ' : ''}${n.body ?? ''}`.slice(0, 160), 'info', link ? { label: 'Open', run: () => { location.href = link; } } : undefined);
    navigator.serviceWorker?.ready.then(reg => reg.showNotification(n.title || 'TradeView', {
      body: n.body || '', icon: '/icons/icon-192.png', badge: '/icons/icon-192.png', tag: `tv-${payload.data?.kind || 'alert'}`, data: { link: link || '/' },
    })).catch(() => {});
  });
}
